import React, { useState } from "react";
import { Check, X, AlertCircle, CheckCircle } from "lucide-react";
import { pricingPlans } from "../../data/mock";
import { joinWaitlist } from "../../services/api";
import { Input } from "../ui/input";

export const Pricing = () => {
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState({ name: "", email: "" });
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [waitlistError, setWaitlistError] = useState(null);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setWaitlistLoading(true);
    setWaitlistError(null);

    try {
      await joinWaitlist({
        name: waitlistForm.name || null,
        email: waitlistForm.email,
      });
      setWaitlistSuccess(true);
    } catch (err) {
      const message =
        err?.response?.data?.detail || "Failed to join waitlist. Please try again.";
      setWaitlistError(message);
    } finally {
      setWaitlistLoading(false);
    }
  };

  const closeModal = () => {
    setShowWaitlistModal(false);
    setWaitlistForm({ name: "", email: "" });
    setWaitlistSuccess(false);
    setWaitlistError(null);
  };

  const startStripeCheckout = async (planKey) => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = `/login?next=${encodeURIComponent("/pricing")}`;
      return;
    }

    const backend =
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_BACKEND_URL?.trim?.() ||
      "";

    if (!backend) {
      alert("Backend URL not configured (REACT_APP_BACKEND_URL).");
      return;
    }

    try {
      const res = await fetch(
        `${backend.replace(/\/$/, "")}/api/subscription/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ plan: planKey }), // "pro_monthly" or "pro_annual"
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        alert(data?.detail || data?.message || "Checkout failed");
        return;
      }

      if (!data?.url) {
        alert("No checkout URL returned");
        return;
      }

      window.location.href = data.url;
    } catch (err) {
      console.error(err);
      alert("Checkout failed");
    }
  };

  const handlePlanClick = (plan) => {
    // If your data/mock marks paid plans with `id: "pro"` or `highlighted: true`,
    // we treat that as Stripe checkout.
    if (plan.id === "pro") {
      // default to monthly unless your plan has a field to choose annual
      // If your mock provides something like plan.billingCycle, adapt here.
      startStripeCheckout("pro_monthly");
      return;
    }

    // Free tier -> send to register (or you can send to /login)
    window.location.href = "/register";
  };

  return (
    <>
      <section
        id="pricing"
        className="bg-black py-24 lg:py-32 border-t border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Section Header */}
          <div className="max-w-2xl mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-white leading-tight tracking-tight mb-4">
              Simple Pricing
            </h2>
            <p className="text-white/60 text-lg leading-relaxed">
              Start free, upgrade when you need more.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl">
            {pricingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`relative p-8 border transition-all duration-400 ${
                  plan.highlighted
                    ? "border-[#C9A962]/50 bg-[#C9A962]/5"
                    : "border-white/10 bg-white/[0.02] hover:border-white/20"
                }`}
              >
                {/* Highlighted Badge */}
                {plan.highlighted && (
                  <div className="absolute -top-3 left-8 px-3 py-1 bg-[#C9A962] text-black text-xs font-semibold">
                    RECOMMENDED
                  </div>
                )}

                {/* Plan Header */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-3xl font-semibold text-white">
                      {plan.price}
                    </span>
                    {plan.period && (
                      <span className="text-white/40 text-sm">/{plan.period}</span>
                    )}
                  </div>
                  <p className="text-white/60 text-sm">{plan.description}</p>
                </div>

                {/* Features */}
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check
                        size={18}
                        className="text-[#C9A962] mt-0.5 flex-shrink-0"
                      />
                      <span className="text-white/80 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <button
                  onClick={() => handlePlanClick(plan)}
                  className={`w-full py-4 font-medium text-base transition-all duration-400 ${
                    plan.highlighted
                      ? "bg-[#C9A962] text-black hover:bg-[#C9A962]/10 hover:text-[#C9A962]"
                      : "bg-white/10 text-white hover:bg-white hover:text-black"
                  }`}
                >
                  {plan.cta}
                </button>

                {/* Optional: waitlist for Pro if you want it */}
                {plan.id === "pro" && (
                  <button
                    onClick={() => setShowWaitlistModal(true)}
                    className="w-full mt-3 py-3 border border-white/15 text-white/80 hover:text-white hover:border-white/30 text-sm transition-colors"
                  >
                    Join the Pro Waitlist
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Waitlist Modal */}
      {showWaitlistModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={closeModal}
          />

          {/* Modal Content */}
          <div className="relative w-full max-w-md bg-[#0a0a0a] border border-white/10 p-8">
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>

            {waitlistSuccess ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 flex items-center justify-center bg-[#C9A962]/10 mx-auto mb-6">
                  <CheckCircle size={32} className="text-[#C9A962]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  You&apos;re on the list!
                </h3>
                <p className="text-white/60 mb-6">
                  We&apos;ll notify you when Pro is available.
                </p>
                <button
                  onClick={closeModal}
                  className="text-[#C9A962] hover:underline text-sm"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-semibold text-white mb-2">
                  Join the Pro Waitlist
                </h3>
                <p className="text-white/60 text-sm mb-6">
                  Be the first to know when Pro is available.
                </p>

                {waitlistError && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 flex items-start gap-3 mb-4">
                    <AlertCircle
                      size={18}
                      className="text-red-500 flex-shrink-0 mt-0.5"
                    />
                    <p className="text-red-400 text-sm">{waitlistError}</p>
                  </div>
                )}

                <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="waitlist-name"
                      className="block text-white text-sm font-medium mb-2"
                    >
                      Name <span className="text-white/40">(optional)</span>
                    </label>
                    <Input
                      id="waitlist-name"
                      type="text"
                      maxLength={100}
                      value={waitlistForm.name}
                      onChange={(e) =>
                        setWaitlistForm((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#C9A962] focus:ring-[#C9A962]/20 rounded-none h-11"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="waitlist-email"
                      className="block text-white text-sm font-medium mb-2"
                    >
                      Email <span className="text-red-400">*</span>
                    </label>
                    <Input
                      id="waitlist-email"
                      type="email"
                      required
                      value={waitlistForm.email}
                      onChange={(e) =>
                        setWaitlistForm((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#C9A962] focus:ring-[#C9A962]/20 rounded-none h-11"
                      placeholder="your@email.com"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={waitlistLoading}
                    className="w-full py-3 bg-[#C9A962] text-black font-medium text-sm transition-all duration-400 hover:bg-[#C9A962]/10 hover:text-[#C9A962] disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                  >
                    {waitlistLoading ? "Joining..." : "Join Waitlist"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
